import React from 'react';
import { TrendingUp, ChartBar, Shield } from 'lucide-react';

/**
 * Компонент секции выбора инвестиционной стратегии
 */
const InvestmentStrategySection = ({ 
  formData, 
  handleInputChange, 
  errors,
  investmentStrategies,
  riskToleranceLevels,
  assetTypes
}) => {
  return (
    <div className="mb-6 bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-200 dark:border-indigo-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <ChartBar className="w-5 h-5 mr-2 text-indigo-500" />
        Инвестиционная стратегия
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Выбор стратегии инвестирования */}
        <div>
          <label htmlFor="investmentStrategy" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Стратегия инвестирования
          </label>
          <select
            id="investmentStrategy"
            name="investmentStrategy"
            value={formData.investmentStrategy}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.investmentStrategy ? 'input-error' : ''}`}
          >
            {investmentStrategies.map((strategy) => (
              <option key={strategy.id} value={strategy.id}>
                {strategy.name} ({strategy.expectedReturn}%, риск: {strategy.risk})
              </option>
            ))}
          </select>
          {errors.investmentStrategy && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.investmentStrategy}
            </p>
          )}
        </div>

        {/* Отношение к риску */}
        <div>
          <label htmlFor="riskTolerance" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Shield className="w-4 h-4 mr-2 text-indigo-500" />
            Отношение к риску
          </label>
          <select
            id="riskTolerance"
            name="riskTolerance"
            value={formData.riskTolerance}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.riskTolerance ? 'input-error' : ''}`}
          >
            {riskToleranceLevels.map((level) => (
              <option key={level.id} value={level.id}>
                {level.name} - {level.description}
              </option>
            ))}
          </select>
          {errors.riskTolerance && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.riskTolerance}
            </p>
          )}
        </div>

        {/* Кастомная доходность инвестиций */}
        <div>
          <label htmlFor="investmentReturn" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <TrendingUp className="w-4 h-4 mr-2 text-indigo-500" />
            Ожидаемая доходность инвестиций (%)
          </label>
          <input
            type="text"
            id="investmentReturn"
            name="investmentReturn"
            value={formData.investmentReturn}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.investmentReturn ? 'input-error' : ''}`}
            placeholder="Введите процент"
            disabled={formData.investmentStrategy !== 'custom'}
          />
          {errors.investmentReturn && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.investmentReturn}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Годовая доходность ваших инвестиций (доступно при выборе "Собственной" стратегии)
          </p>
        </div>

        {/* Доходность пассивного дохода */}
        <div>
          <label htmlFor="passiveIncomeReturn" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Доходность пассивного дохода (%)
          </label>
          <input
            type="text"
            id="passiveIncomeReturn"
            name="passiveIncomeReturn"
            value={formData.passiveIncomeReturn}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.passiveIncomeReturn ? 'input-error' : ''}`}
            placeholder="Введите процент"
          />
          {errors.passiveIncomeReturn && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.passiveIncomeReturn}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Ожидаемая доходность после выхода на пенсию (обычно ниже основной)
          </p>
        </div>

        {/* Ставка налога */}
        <div>
          <label htmlFor="taxRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Ставка налога (%)
          </label>
          <input
            type="text"
            id="taxRate"
            name="taxRate"
            value={formData.taxRate}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.taxRate ? 'input-error' : ''}`}
            placeholder="Введите процент"
          />
          {errors.taxRate && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.taxRate}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            НДФЛ на инвестиционный доход (обычно 13% в России)
          </p>
        </div>

        {/* Учет сложного процента */}
        <div className="col-span-full">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              name="includeCompounding"
              checked={formData.includeCompounding}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Учитывать сложный процент при расчетах
            </span>
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
            Реинвестирование полученного дохода значительно увеличивает итоговый капитал
          </p>
        </div>
      </div>

      {/* Выбор активов для включения в расчет */}
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Учитывать следующие активы в расчете:
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {assetTypes.map((asset) => (
            <label key={asset.id} className="inline-flex items-start p-2 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
              <input
                type="checkbox"
                name={`asset_${asset.id}`}
                checked={formData.consideredAssets.includes(asset.id)}
                onChange={handleInputChange}
                className="form-checkbox mt-0.5"
              />
              <div className="ml-2">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300">{asset.name}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {asset.expectedReturn}%, риск: {asset.risk}
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InvestmentStrategySection;